import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SeoHead from "./SiteSeo";
import SiteLayout from "./SiteLayout";
import { SmartLink } from "./SiteHeader";
import { FinalCta } from "./sections";
import { VipClubForm } from "./VipClubForm";

const API = process.env.REACT_APP_BACKEND_URL;
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : "");
const topicImg = (topic) => `/brand/vault/${topic || "first-time-buyers"}.jpg`;

export function VaultCard({ p, big = false }) {
  return (
    <SmartLink className={`vault-card${big ? " big" : ""}`} href={`/strategy-vault/${p.slug}/`} data-testid={`vault-card-${p.slug}`}>
      <div className={`vault-card-img${p.hero_image?.startsWith("/brand/") ? " is-illo" : ""}`}><img src={p.hero_image ? (p.hero_image.startsWith("/api") ? `${API}${p.hero_image}` : p.hero_image) : topicImg(p.topic)} alt="" loading="lazy" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = topicImg(p.topic); }} /></div>
      <div className="vault-card-body">
        <span className="tag">{p.topic_name}</span>
        <h3>{p.title}</h3>
        {big && p.summary && <p>{p.summary.replace(/ - read more$/, "")}</p>}
        <small>{fmtDate(p.published_at)} · {p.reading_minutes} min read</small>
      </div>
    </SmartLink>
  );
}

export function TopicChips({ topics, active, basePath = "/strategy-vault/" }) {
  return (
    <div className="vault-chips" data-testid="vault-topic-chips">
      <SmartLink href={basePath} className={!active ? "active" : ""} data-testid="vault-chip-all">All articles</SmartLink>
      {topics.map((t) => <SmartLink key={t.slug} href={`/strategy-vault/topic/${t.slug}/`} className={active === t.slug ? "active" : ""} data-testid={`vault-chip-${t.slug}`}>{t.name}<b>{t.count}</b></SmartLink>)}
    </div>
  );
}


// Vault hub hero badge — Julie's-style record-sleeve badge, Paula Abdul "Straight Up" (broker pick 2026-09-13).
export function VaultBadge() {
  return (
    <div className="vault-badge" data-testid="vault-badge" aria-hidden="true">
      <svg className="vault-badge-arch vault-badge-arch-tight" viewBox="0 0 900 200" style={{ overflow: "visible" }}><defs><path id="vault-arc" d="M 40 190 A 720 720 0 0 1 860 190" /></defs>
        <text className="vault-badge-arch-txt"><textPath href="#vault-arc" startOffset="50%" textAnchor="middle">STRAIGHT UP</textPath></text></svg>
      <div className="vault-badge-mid">
        <div className="vault-badge-side l">Now tell me</div>
        <img className="vault-badge-img" src="/brand/vault/icons/cassette.png" alt="" loading="eager" />
        <div className="vault-badge-side r">Do you really<br />wanna know</div>
      </div>
    </div>
  );
}

import { TOPIC_BADGES, TopicBadge } from "./vaultBadges";

export default function VaultHub() {
  const parts = useLocation().pathname.split("/").filter(Boolean);
  const topicSlug = parts[1] === "topic" ? parts[2] : null;
  const { search } = useLocation();
  const navigate = useNavigate();
  const q0 = new URLSearchParams(search).get("q") || "";
  const [q, setQ] = useState(q0);
  const [topics, setTopics] = useState([]);
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => { fetch(`${API}/api/public/vault/topics`).then((r) => r.json()).then((d) => setTopics(d.topics)).catch(() => {}); }, []);
  useEffect(() => { setPage(1); setItems([]); }, [topicSlug, q0]);
  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ page: String(page), limit: "12" });
    if (topicSlug) qs.set("topic", topicSlug);
    if (q0) qs.set("q", q0);
    fetch(`${API}/api/public/vault?${qs}`).then((r) => r.json()).then((d) => { setData(d); setItems((prev) => (page === 1 ? d.items : [...prev, ...d.items])); }).catch(() => {}).finally(() => setLoading(false));
  }, [topicSlug, q0, page]);

  const onSearch = (v) => {
    setQ(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => navigate({ pathname: topicSlug ? `/strategy-vault/topic/${topicSlug}/` : "/strategy-vault/", search: v.trim() ? `?q=${encodeURIComponent(v.trim())}` : "" }, { replace: true }), 350);
  };

  const topic = data?.topic || topics.find((t) => t.slug === topicSlug) || null;
  if (topicSlug && topics.length && !topic) return <SiteLayout><section className="calc-hero"><div className="wrap"><h1>That topic doesn’t exist.</h1><SmartLink className="btn" href="/strategy-vault/">Back to the Vault</SmartLink></div></section></SiteLayout>;
  const featured = !q0 && page === 1 ? items.slice(0, 3) : [];
  const rest = featured.length ? items.slice(3) : items;
  const title = topic ? `${topic.name} — Strategy Vault | Hello Mortgage Team` : "Strategy Vault — Mortgage Advice Without the Jargon | Hello Mortgage Team";
  const desc = topic ? topic.blurb : "300+ plain-English articles on buying, renewing, refinancing and investing in Alberta. Straight talk from the Hello Mortgage Team.";

  return (
    <SiteLayout>
      <SeoHead title={title} description={desc} path={topicSlug ? `/strategy-vault/topic/${topicSlug}/` : "/strategy-vault/"} />
      <section className="calc-hero vault-hero vault-hero-badge" data-testid={topicSlug ? `vault-topic-${topicSlug}` : "vault-hub"}>
        <div className="wrap">
          {topic && <SmartLink className="textlink vault-back" href="/strategy-vault/">← All of the Vault</SmartLink>}
          {topic ? (TOPIC_BADGES[topic.slug] ? <TopicBadge badge={TOPIC_BADGES[topic.slug]} /> : <div className="eyebrow">Strategy Vault · {topic.name}</div>) : <VaultBadge />}
          {!topic && new URLSearchParams(window.location.search).get("badges") === "all" && (
            <div className="vault-badge-sheet" data-testid="vault-badge-sheet">{Object.entries(TOPIC_BADGES).map(([k, b]) => <TopicBadge key={k} badge={b} />)}</div>
          )}
          <h1 data-testid="vault-h1" className="vault-h1-line">{topic ? `${topic.name} · ${TOPIC_BADGES[topic.slug]?.line || "Strategy Vault"}` : "Straight talk on mortgages, minus the jargon."}</h1>
          <p>{topic ? topic.blurb : "Every article we’ve written for Albertans — buying, renewing, refinancing, investing and what the Bank of Canada is up to. Search it, browse by topic, or just start reading."}</p>
          <label className="vault-search" htmlFor="vault-q">
            <input id="vault-q" type="search" placeholder="Search the Vault… e.g. penalty, FHSA, renewal" value={q} onChange={(e) => onSearch(e.target.value)} data-testid="vault-search" />
            <span>{data ? `${data.total} article${data.total === 1 ? "" : "s"}` : ""}</span>
          </label>
          <TopicChips topics={topics} active={topicSlug} />
        </div>
      </section>
      <section className="vault-body">
        <div className="wrap">
          {featured.length > 0 && <div className="vault-featured" data-testid="vault-featured">{featured.map((p) => <VaultCard key={p.slug} p={p} big />)}</div>}
          {rest.length > 0 && <div className="vault-grid" data-testid="vault-grid">{rest.map((p) => <VaultCard key={p.slug} p={p} />)}</div>}
          {data && data.total === 0 && <div className="vault-empty" data-testid="vault-empty"><h3>{q0 ? `Nothing in the Vault for “${q0}” yet.` : "Nothing filed here yet."}</h3><p>{q0 ? "Try a shorter word — or just ask us. Humans are faster than search." : "We’re writing it. In the meantime, the team is one text away."}</p><SmartLink className="btn" href="/contact/">Ask the team →</SmartLink></div>}
          {data && page < data.pages && <div className="vault-more"><button type="button" className="btn outline" onClick={() => setPage(page + 1)} disabled={loading} data-testid="vault-load-more">{loading ? "Loading…" : `Load more (${data.total - items.length} left)`}</button></div>}
        </div>
      </section>
      <section className="vault-vip"><div className="wrap"><VipClubForm /></div></section>
      <FinalCta />
    </SiteLayout>
  );
}
